export interface RecordingResult {
  blob: Blob
  durationSeconds: number
}

export class AudioRecorder {
  private mediaRecorder: MediaRecorder | null = null
  private chunks: Blob[] = []
  private startTime = 0
  private stream: MediaStream | null = null
  private audioContext: AudioContext | null = null
  private analyserNode: AnalyserNode | null = null
  private sourceNode: MediaStreamAudioSourceNode | null = null

  async start(): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true })

    // Set up Web Audio analyser for real-time amplitude data
    this.audioContext = new AudioContext()
    this.sourceNode = this.audioContext.createMediaStreamSource(this.stream)
    this.analyserNode = this.audioContext.createAnalyser()
    this.analyserNode.fftSize = 256
    this.analyserNode.smoothingTimeConstant = 0.7
    this.sourceNode.connect(this.analyserNode)

    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : 'audio/mp4'

    this.mediaRecorder = new MediaRecorder(this.stream, { mimeType })
    this.chunks = []
    this.startTime = Date.now()

    this.mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        this.chunks.push(e.data)
      }
    }

    this.mediaRecorder.start(250) // Collect data every 250ms
  }

  stop(): Promise<RecordingResult> {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') {
        reject(new Error('Not recording'))
        return
      }

      this.mediaRecorder.onstop = () => {
        const blob = new Blob(this.chunks, {
          type: this.mediaRecorder?.mimeType || 'audio/webm',
        })
        const durationSeconds = Math.round((Date.now() - this.startTime) / 1000)

        // Clean up audio context and tracks
        this.cleanupAudio()

        resolve({ blob, durationSeconds })
      }

      this.mediaRecorder.onerror = () => {
        this.cleanupAudio()
        reject(new Error('Recording failed'))
      }

      this.mediaRecorder.stop()
    })
  }

  private cleanupAudio(): void {
    this.sourceNode?.disconnect()
    this.sourceNode = null
    this.analyserNode = null
    this.audioContext?.close().catch(() => {})
    this.audioContext = null
    this.stream?.getTracks().forEach((t) => t.stop())
    this.stream = null
  }

  get isRecording(): boolean {
    return this.mediaRecorder?.state === 'recording'
  }

  getElapsedSeconds(): number {
    if (!this.startTime) return 0
    return Math.round((Date.now() - this.startTime) / 1000)
  }

  /** Returns a normalized amplitude value between 0 and 1 */
  getAmplitude(): number {
    if (!this.analyserNode) return 0
    const data = new Uint8Array(this.analyserNode.frequencyBinCount)
    this.analyserNode.getByteFrequencyData(data)
    let sum = 0
    for (let i = 0; i < data.length; i++) {
      sum += data[i]
    }
    return sum / (data.length * 255)
  }
}
